import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../comun/services/grpc/mapConnectError";
import { createCachedFactory } from "../../../utils/factoryUtils";
import { EstadisticaLoteItem, SateliteService } from "../../../generated/grpc/satelite_pb";
import { grpcSateliteDatabase } from "../database/GrpcSateliteDatabase";
import { serviceSync } from "../../../base/services/ServiceSync";
import { EstadisticaLoteResponse } from "../response/EstadisticaLoteResponse";

/** Item guardado localmente: `EstadisticaLoteItem` (gRPC) + `fincaLoteId`
 *  inyectado acá -mismo criterio que `StoredEscenaItem` en `EscenaGrpcService.ts`-,
 *  hace falta como propiedad real para el índice `fincaLoteId` de
 *  `GrpcSateliteDatabase`. */
type StoredEstadisticaLoteItem = EstadisticaLoteItem & { fincaLoteId: string };

/** Variante gRPC-Web (con delta real, por lote) de `EstadisticaLoteGetListByLoteIdService`
 *  (ver ese archivo). Devuelve `EstadisticaLoteResponse[]` -mismo shape que ya
 *  usa el resto de la app-. Rellena con valores inertes los campos que se
 *  comprobó que nadie lee en toda la app (`fincaLoteId`, `sceneId`, `cloud`,
 *  `max`, `min`, `p10`, `p90`, `std`, `median`, `variance` — el chart de
 *  tendencia solo usa `q1`/`q3`/`average`, ver el comentario de
 *  `EstadisticaLoteItem` en el proto). */
class EstadisticaLoteGrpcService {
  constructor(private readonly _loteId: string) {}

  async getListAsync(): Promise<EstadisticaLoteResponse[]> {
    await this._syncAsync();
    const items = await grpcSateliteDatabase.getEstadisticasByLoteIdAsync<StoredEstadisticaLoteItem>(
      this._loteId,
    );
    return items.map(toEstadisticaLoteResponse);
  }

  clearCache(): void {
    serviceSync.reset(`grpc:estadisticas:${this._loteId}`);
  }

  private async _syncAsync(): Promise<void> {
    await serviceSync.run(`grpc:estadisticas:${this._loteId}`, async () => {
      const ultimaSincronizacion = await grpcSateliteDatabase.getUltimaSincronizacionEstadisticasAsync(
        this._loteId,
      );
      const client = createClient(SateliteService, grpcTransportProvider.get());

      let response;
      try {
        response = await client.listEstadisticasByLoteId({
          loteId: this._loteId,
          ultimaSincronizacion,
        });
      } catch (error) {
        throw mapConnectError(error);
      }

      const items: StoredEstadisticaLoteItem[] = response.items.map((item) => ({
        ...item,
        fincaLoteId: this._loteId,
      }));
      await grpcSateliteDatabase.putEstadisticasDeltaAsync(
        this._loteId,
        items,
        response.timestampSincronizacion,
      );
    });
  }
}

/** Factory cacheada: una única instancia por loteId (comparte cooldown/caché,
 *  mismo criterio que `fx_createEstadisticaLoteGetListByLoteIdService`). */
export const fx_createEstadisticaLoteGrpcService = createCachedFactory(
  (loteId: string) => new EstadisticaLoteGrpcService(loteId),
);

function toEstadisticaLoteResponse(
  estadistica: EstadisticaLoteItem,
): EstadisticaLoteResponse {
  return {
    id: estadistica.id,
    isEliminado: estadistica.eliminado,
    fincaLoteId: "",
    sceneId: "",
    fecha: estadistica.fecha,
    indiceId: estadistica.indiceId,
    cloud: 0,
    q1: estadistica.q1,
    q3: estadistica.q3,
    max: 0,
    min: 0,
    p10: 0,
    p90: 0,
    std: 0,
    median: 0,
    average: estadistica.average,
    variance: 0,
  };
}
